import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSession, login as loginUser } from '../services/auth';
import { login as loginAdmin, isAdminLoggedIn } from '../services/adminAuth';
import '../admin/Admin.css';

const ROLE_CONTENT = {
  user: {
    title: 'User Sign In',
    subtitle: 'Sign in to access the ChemRobotics AI Chatbot',
    placeholder: 'Enter your email address',
    usernameLabel: 'Email address',
    usernameType: 'email',
    destination: '/chat',
  },
  admin: {
    title: 'Admin Panel',
    subtitle: 'Sign in to manage chatbot questions',
    placeholder: 'Enter admin username',
    usernameLabel: 'Username',
    usernameType: 'text',
    destination: '/admin/dashboard',
  },
};

export default function LoginPage({ defaultRole = 'user' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get('role') === 'admin' ? 'admin' : defaultRole;
  const [role, setRole] = useState(initialRole);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const content = ROLE_CONTENT[role];

  useEffect(() => {
    const activeSession = getSession();
    if (activeSession?.role === 'user') {
      navigate('/chat', { replace: true });
    } else if (isAdminLoggedIn()) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const switchRole = (nextRole) => {
    setRole(nextRole);
    setError('');
    setUsername('');
    setPassword('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      if (role === 'admin') {
        await loginAdmin(username.trim(), password);
        navigate('/admin/dashboard', { replace: true });
      } else {
        const ok = await loginUser('user', username.trim(), password);
        if (ok) {
          navigate('/chat', { replace: true });
        } else {
          setError('Invalid email or password.');
        }
      }
    } catch (loginError) {
      setError(loginError.message || 'Unable to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-root admin-login">
      <div className="admin-login__card">
        <img
          className="admin-login__logo"
          src={`${process.env.PUBLIC_URL}/assets/pharmvetpat-logo.svg`}
          alt="PharmVetPat by ChemRobotics"
        />
        <div className="login-role-tabs" role="tablist" aria-label="Account type">
          <button
            className={`login-role-tabs__item ${role === 'user' ? 'is-active' : ''}`}
            onClick={() => switchRole('user')}
            type="button"
            role="tab"
            aria-selected={role === 'user'}
          >
            User
          </button>
          <button
            className={`login-role-tabs__item ${role === 'admin' ? 'is-active' : ''}`}
            onClick={() => switchRole('admin')}
            type="button"
            role="tab"
            aria-selected={role === 'admin'}
          >
            Admin
          </button>
        </div>
        <h1 className="admin-login__title">{content.title}</h1>
        <p className="admin-login__sub">{content.subtitle}</p>

        {error && <div className="admin-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="admin-form__group">
            <label className="admin-form__label" htmlFor="username">{content.usernameLabel}</label>
            <input
              id="username"
              className="admin-form__input"
              type={content.usernameType}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder={content.placeholder}
              autoComplete={role === 'user' ? 'email' : 'username'}
              required
            />
          </div>
          <div className="admin-form__group">
            <label className="admin-form__label" htmlFor="password">Password</label>
            <input
              id="password"
              className="admin-form__input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              required
            />
          </div>
          <button type="submit" className="admin-btn admin-btn--primary admin-btn--block" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : `Sign in as ${role === 'admin' ? 'Admin' : 'User'}`}
          </button>
        </form>
      </div>
    </div>
  );
}
